import { BoundingBox } from '@vladmandic/face-api';

import type { FrameTriggerConfig } from 'lib/clients/FrameTrigger';
import type { StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import { isLowLightForDocument } from 'verify/lib/utils/document';
import type { LuminosityData } from 'verify/lib/utils/image';

export enum GovernmentIdTriggerType {
  DetectedGlare,
  DetectedLowLight,
  Capture,
  HoldStill,
  MoveCloser,
  None,
}

export interface GovernmentIdTriggerParams {
  currentTrigger: GovernmentIdTriggerType;
  faceBoundingBox: BoundingBox;
  glareRatio: number;
  luminosityData: LuminosityData | null;
  isPassport: boolean;
}

// Was previously 100. Increased to 250 to reduce resource consumption.
export const DETECT_INTERVAL = 250;
export const SIDE_THRESHOLD = 0.3;
export const FACE_WIDTH = 0.27;
export const FACE_HEIGHT = 0.32;

export const PASSPORT_SIDE_THRESHOLD = 0.25;
export const PASSPORT_FACE_WIDTH = 0.3;
export const PASSPORT_FACE_HEIGHT = 0.37;

export const GOVERNMENT_ID_BACK_TRIGGER_CONFIGS: Array<
  FrameTriggerConfig<
    GovernmentIdTriggerType,
    GovernmentIdTriggerParams,
    StepGovernmentIdConfig['localizations']['capture-page']
  >
> = [
  {
    type: GovernmentIdTriggerType.None,
    shouldShow: () => true,
    showAfterFrames: 0,
    hideAfterFrames: 0,
    getPrompt: () => '',
  },
];

export const GOVERNMENT_ID_TRIGGER_CONFIGS: Array<
  FrameTriggerConfig<
    GovernmentIdTriggerType,
    GovernmentIdTriggerParams,
    StepGovernmentIdConfig['localizations']['capture-page']
  >
> = [
  {
    type: GovernmentIdTriggerType.DetectedGlare,
    shouldShow: detectsGlare,
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.detected-glare'),
  },
  {
    // This trigger is just used to trigger the screenshot
    type: GovernmentIdTriggerType.Capture,
    shouldShow: (params) => params.currentTrigger === GovernmentIdTriggerType.HoldStill,
    showAfterFrames: 5,
    hideAfterFrames: 999999,
    getPrompt: ({ t }) => t('government-id-scanner.hint.hold-still'),
  },
  {
    type: GovernmentIdTriggerType.HoldStill,
    shouldShow: matchesGovernmentId,
    showAfterFrames: 2,
    hideAfterFrames: 2,
    getPrompt: ({ t }) => t('government-id-scanner.hint.hold-still'),
  },
  {
    type: GovernmentIdTriggerType.MoveCloser,
    shouldShow: matchesZoomedGovernmentId,
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-closer'),
  },
  {
    type: GovernmentIdTriggerType.DetectedLowLight,
    shouldShow: detectsLowLight,
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.detected-low-light'),
  },
  {
    type: GovernmentIdTriggerType.None,
    shouldShow: () => true,
    showAfterFrames: 0,
    hideAfterFrames: 0,
    getPrompt: () => '',
  },
];

export function detectsGlare(params: GovernmentIdTriggerParams) {
  // NB: this is looser than should capture's threshold
  return (
    params.glareRatio > 0.0002 && (matchesZoomedGovernmentId(params) || matchesGovernmentId(params))
  );
}

export function detectsLowLight(params: GovernmentIdTriggerParams): boolean {
  return isLowLightForDocument(params.luminosityData);
}

export function matchesZoomedGovernmentId(params: GovernmentIdTriggerParams) {
  const { faceBoundingBox } = params;

  if (faceBoundingBox == null) {
    return false;
  }

  const scale = 1.5;
  const scaledOutBox = new BoundingBox(
    (faceBoundingBox.left - 0.5) * scale + 0.5,
    (faceBoundingBox.top - 0.5) * scale + 0.5,
    (faceBoundingBox.right - 0.5) * scale + 0.5,
    (faceBoundingBox.bottom - 0.5) * scale + 0.5,
  );

  return matchesGovernmentId({ ...params, faceBoundingBox: scaledOutBox });
}

export function matchesGovernmentId({ isPassport, faceBoundingBox }: GovernmentIdTriggerParams) {
  if (faceBoundingBox == null) {
    return false;
  }

  if (isPassport) {
    const centerX = faceBoundingBox.left + faceBoundingBox.width / 2;

    return (
      // Unlike driver licenses, portraits must be on the left side.
      centerX < PASSPORT_SIDE_THRESHOLD &&
      faceBoundingBox.width < PASSPORT_FACE_WIDTH + 0.05 &&
      faceBoundingBox.height < PASSPORT_FACE_HEIGHT + 0.05
    );
  } else {
    const centerX = faceBoundingBox.left + faceBoundingBox.width / 2;

    return (
      (centerX < SIDE_THRESHOLD || centerX > 1 - SIDE_THRESHOLD) &&
      faceBoundingBox.width < FACE_WIDTH + 0.05 &&
      faceBoundingBox.height < FACE_HEIGHT + 0.05
    );
  }
}
